import { computed, ref } from 'vue'
import { endSession, fetchSessionProfile, startGoogleLogin } from '@/lib/auth'
import type { GoogleProfile } from '@/lib/auth'

// Module-level so every component sharing this composable sees the same
// signed-in state and the session is only fetched once per page load.
const profileState = ref<GoogleProfile | null>(null)
const loaded = ref(false)
let loadPromise: Promise<void> | null = null

function loadOnce(): Promise<void> {
  if (!loadPromise) {
    loadPromise = fetchSessionProfile().then((p) => {
      profileState.value = p
      loaded.value = true
    })
  }
  return loadPromise
}

export function useGoogleAuth() {
  void loadOnce()

  const profile = computed<GoogleProfile>(() => profileState.value || {})
  const isSignedIn = computed(() => profileState.value !== null)

  const signOut = async () => {
    await endSession()
    profileState.value = null
  }

  return {
    profile,
    isSignedIn,
    loaded: computed(() => loaded.value),
    /** Resolves once the initial /api/auth/me check has settled - await this before trusting isSignedIn for a one-shot check (e.g. on mount), since the session check is async and isSignedIn starts false until it resolves. */
    whenReady: loadOnce,
    signIn: startGoogleLogin,
    signOut,
  }
}
